import {tx, ptx, usePageText} from '../lib/page-text';
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "../lib/localized-router";
import { api } from "../lib/api-client";
import { DashboardDialog } from "../components/DashboardDialog";
import { useAuth } from "../lib/auth-store";
import { ImageUpload } from "../components/ImageUpload";
import { CommerceOrders } from "../components/CommerceOrders";
import {
  PlusIcon,
  CatalogIcon,
  SessionsIconNav,
  ProjectsIcon,
} from "../components/icons";
import { useT } from "../lib/i18n";

type Kind = "product" | "service";
interface Product {
  id: string;
  externalId: string | null;
  name: string;
  description: string | null;
  imageUrl: string | null;
  priceCents: number;
  compareAtCents: number | null;
  currency: string;
  stock: number;
  lowStockAt: number;
  active: boolean;
  metadata?: Record<string, any>;
}
interface ConnectStatus {
  onboarded: boolean;
  stripeAccountId: string | null;
  chargesEnabled: boolean;
}
const details = (p?: Product) =>
  p?.metadata?.storefront && typeof p.metadata.storefront === "object"
    ? p.metadata.storefront
    : {};
const kindOf = (p: Product): Kind =>
  details(p).kind === "service" ? "service" : "product";
const collectionOf = (p: Product) =>
  typeof details(p).collection === "string" ? details(p).collection : "";
const digits = (currency: string) =>
  new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions()
    .maximumFractionDigits ?? 2;
const fmt = (amount: number, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(amount / 10 ** digits(currency));
  } catch {
    return `${amount} (${currency})`;
  }
};
const errorText = (e: any) => {
  const text = e?.response?.data?.message ?? e?.message;
  return Array.isArray(text)
    ? text.join(". ")
    : text || "Could not save. Please try again.";
};

/** Existing product records and API contracts also store optional service/collection metadata. */
export function CommercePage() {
  usePageText();
  const { section = "catalog" } = useParams();
  const { t } = useT();
  const routes = ["catalog", "services", "collections", "orders", "settings"];
  const current = routes.includes(section) ? section : "catalog";
  return (
    <div className="commerce-workspace">
      <div className="page-eyebrow">
        <CatalogIcon />
        {t("nav.ecommerce")}
      </div>
      <nav className="commerce-tabs" aria-label={tx("Ecommerce sections")}>
        {routes.map((item, i) => (
          <Link
            key={item}
            to={"/commerce/" + item}
            aria-current={current === item ? "page" : undefined}
          >
            {t(
              [
                "nav.products",
                "nav.services",
                "nav.collections",
                "nav.orders",
                "nav.commerceSettings",
              ][i],
            )}
          </Link>
        ))}
      </nav>
      <CatalogPage key={current} workspaceSection={current} />
    </div>
  );
}

export function CatalogPage({
  streamId,
  archived = false,
  initialSection = "products",
  workspaceSection,
}: {
  streamId?: string;
  archived?: boolean;
  initialSection?: "products" | "orders";
  workspaceSection?: string;
} = {}) {
  usePageText();
  const [section, setSection] = useState(initialSection);
  const canEdit =
    useAuth((s) => ["OWNER", "ADMIN", "MEMBER"].includes(s.user?.role ?? "")) &&
    !archived;
  const [creating, setCreating] = useState<Kind | null>(null),
    [editing, setEditing] = useState<Product | null>(null);
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(""),
    [status, setStatus] = useState("active");
  const [collection, setCollection] = useState(params.get("collection") ?? "");
  const [view, setView] = useState<"grid" | "list">(() => {
    try {
      return localStorage.getItem("kardolive.catalog.view") === "list"
        ? "list"
        : "grid";
    } catch {
      return "grid";
    }
  });
  const orders =
    workspaceSection === "orders" ||
    (!workspaceSection && section === "orders");
  const settings = workspaceSection === "settings";
  const collections = workspaceSection === "collections";
  const service = workspaceSection === "services";
  const list = useQuery({
    queryKey: ["products", false, 500],
    enabled: !orders && !settings,
    queryFn: async () =>
      (
        await api.get<Product[]>("/me/catalog/products", {
          params: { activeOnly: "0", limit: 500 },
        })
      ).data,
    refetchInterval: 30000,
  });
  const items = list.data ?? [];
  const collectionNames = [
    ...new Set(items.map(collectionOf).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));
  const filtered = items.filter(
    (p) =>
      (streamId ||
        !workspaceSection ||
        collections ||
        kindOf(p) === (service ? "service" : "product")) &&
      (status === "all" || p.active === (status === "active")) &&
      (!collection || collectionOf(p) === collection) &&
      `${p.name} ${p.externalId ?? ""} ${p.description ?? ""}`
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  useEffect(() => {
    setCollection(params.get("collection") ?? "");
  }, [params]);
  const title = streamId
    ? "Products & orders"
    : orders
      ? "Orders"
      : settings
        ? "Commerce settings"
        : collections
          ? "Collections"
          : service
            ? "Services"
            : "Products";
  return (
    <div className="catalog-workspace">
      <div className="workspace-heading">
        <div>
          <h1 className="h1">{title}</h1>
          <p className="subtitle">
            {orders
              ? tx("Review purchases, payment status and fulfillment.")
              : settings
                ? tx("Manage how your workspace accepts payments.")
                : collections
                  ? tx("Group products and services with a shared collection label.")
                  : service
                    ? tx("Sell consultations, lessons and other services. Arrange delivery directly with your customer.")
                    : tx("Manage your catalog once. Feature items in any enabled live stream.")}
          </p>
        </div>
        {!orders && !settings && !collections && (
          <button
            className="btn primary"
            disabled={!canEdit || list.isError || list.isPending}
            onClick={(e) => {
              e.currentTarget.focus();
              setCreating(service ? "service" : "product");
            }}
          >
            <PlusIcon size={16} />
            {service ? tx("Add service") : tx("Add product")}
          </button>
        )}
      </div>
      {!workspaceSection && (
        <div className="tabs">
          <button
            className={`tab ${section === "products" ? "active" : ""}`}
            onClick={() => setSection("products")}
          >{tx("Products")}</button>
          <button
            className={`tab ${section === "orders" ? "active" : ""}`}
            onClick={() => setSection("orders")}
          >{tx("Orders & sales")}</button>
        </div>
      )}
      {!canEdit && (
        <p className="muted">{tx("You have read-only access to this catalog.")}</p>
      )}
      {settings ? (
        <CommerceSettings canEdit={canEdit} />
      ) : orders ? (
        <CommerceOrders streamId={streamId} canEdit={canEdit} />
      ) : (
        <>
          {service && (
            <p className="commerce-note">{tx("Service purchases do not reserve appointment slots. Include your delivery and scheduling instructions in the description shown to customers.")}</p>
          )}
          {list.isPending && (
            <p role="status" className="card">{tx("Loading catalog…")}</p>
          )}
          {list.isError && (
            <p role="alert" className="error">{ptx("Catalog could not load.{v0}{v1}{v2}{v3}", {v0: (" "), v1: (list.data ? tx("Showing the last loaded items.") : ""), v2: (" "), v3: (<button
                className="btn outline sm"
                onClick={() => void list.refetch()}
              >{tx("Retry")}</button>)})}</p>
          )}
          {collections ? (
            <>
              {list.isSuccess && !collectionNames.length && (
                <div className="catalog-empty card">
                  <ProjectsIcon />
                  <h3>{tx("No collections yet")}</h3>
                  <p className="muted">{tx("Open a product or service and enter a collection name. Items with the same label appear together here.")}</p>
                  <Link className="btn outline" to="/commerce/catalog">{tx("Open products")}</Link>
                </div>
              )}
              <div className="catalog-grid">
                {collectionNames.map((name) => {
                  const members = items.filter((p) => collectionOf(p) === name);
                  return (
                    <article className="card collection-card" key={name}>
                      <ProjectsIcon />
                      <h3>{name}</h3>
                      <p className="muted">{ptx("{v0}{v1}products ·{v2}{v3}{v4}services", {v0: (members.filter((p) => kindOf(p) === "product").length), v1: (" "), v2: (" "), v3: (members.filter((p) => kindOf(p) === "service").length), v4: (" ")})}</p>
                      <div className="flex gap">
                        <Link
                          className="btn outline sm"
                          to={
                            "/commerce/catalog?collection=" +
                            encodeURIComponent(name)
                          }
                        >{tx("Products")}</Link>
                        <Link
                          className="btn outline sm"
                          to={
                            "/commerce/services?collection=" +
                            encodeURIComponent(name)
                          }
                        >{tx("Services")}</Link>
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              <div className="catalog-toolbar">
                <input
                  className="input"
                  aria-label={tx("Search catalog")}
                  placeholder={tx("Search name or SKU")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <select
                  className="input"
                  aria-label={tx("Catalog status")}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="active">{tx("Active")}</option>
                  <option value="archived">{tx("Archived")}</option>
                  <option value="all">{tx("All statuses")}</option>
                </select>
                <select
                  className="input"
                  aria-label={tx("Collection")}
                  value={collection}
                  onChange={(e) => {
                    setCollection(e.target.value);
                    if (workspaceSection)
                      setParams(
                        e.target.value ? { collection: e.target.value } : {},
                      );
                  }}
                >
                  <option value="">{tx("All collections")}</option>
                  {collectionNames.map((name) => (
                    <option key={name}>{name}</option>
                  ))}
                </select>
                <div
                  className="catalog-view"
                  role="group"
                  aria-label={tx("Catalog view")}
                >
                  {(["grid", "list"] as const).map((v) => (
                    <button
                      key={v}
                      className="btn outline"
                      aria-pressed={view === v}
                      onClick={() => {
                        setView(v);
                        try {
                          localStorage.setItem("kardolive.catalog.view", v);
                        } catch {
                          /* unavailable */
                        }
                      }}
                    >
                      {v === "grid" ? tx("Grid") : tx("List")}
                    </button>
                  ))}
                </div>
              </div>
              {list.isSuccess && (
                <p className="muted catalog-count">{ptx("{v0} matching {v1}{v2}", {v0: (filtered.length), v1: (service ? tx("services") : tx("items")), v2: (items.length === 500
                    ? tx(" · Searching the 500 most recently updated items")
                    : "")})}</p>
              )}
              {list.isSuccess && !filtered.length && (
                <div className="card catalog-empty">
                  <CatalogIcon />
                  <h3>
                    {items.length
                      ? tx("No matching items")
                      : service
                        ? tx("Add your first service")
                        : tx("Add your first product")}
                  </h3>
                  <p className="muted">
                    {items.length
                      ? tx("Try another search, collection or status.")
                      : tx("Start with a name, image and price. Add more details when you need them.")}
                  </p>
                  {items.length > 0 && (
                    <button
                      className="btn outline"
                      onClick={() => {
                        setSearch("");
                        setStatus("all");
                        setCollection("");
                        if (workspaceSection) setParams({});
                      }}
                    >{tx("Clear filters")}</button>
                  )}
                </div>
              )}
              <div
                className={`catalog-grid ${view === "list" ? "catalog-list" : ""}`}
              >
                {filtered.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onEdit={() => setEditing(p)}
                    canEdit={canEdit}
                    streamId={streamId}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}
      {(creating || editing) && (
        <EditProductModal
          product={editing ?? undefined}
          kind={creating ?? kindOf(editing!)}
          collections={collectionNames}
          onClose={() => {
            setCreating(null);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

export function CommerceSettings({ canEdit }: { canEdit: boolean }) {
  usePageText();
  const status = useQuery({
    queryKey: ["stripe-connect-status"],
    queryFn: async () =>
      (await api.get<ConnectStatus>("/me/commerce/connect/status")).data,
  });
  const start = useMutation({
    mutationFn: async () => {
      const base =
        window.location.origin +
        (window.location.pathname.startsWith("/app/") ? "/app" : "");
      return (
        await api.post("/me/commerce/connect/onboarding-link", {
          returnUrl: base + "/commerce/settings?stripe=ok",
          refreshUrl: base + "/commerce/settings?stripe=refresh",
        })
      ).data;
    },
    onSuccess: (data) => {
      window.location.assign(data.onboardingUrl);
    },
  });
  return (
    <section className="card commerce-payment">
      <div className="row-between">
        <h2>{tx("Payments")}</h2>
        <span
          className={`badge ${status.data?.chargesEnabled ? "success" : ""}`}
        >
          {status.isPending
            ? tx("Checking…")
            : status.isError
              ? tx("Unavailable")
              : status.data?.chargesEnabled
                ? tx("Ready to accept payments")
                : tx("Setup required")}
        </span>
      </div>
      <p className="muted">{tx("Connect your payment account to accept purchases from your live streams. Payment setup opens securely with Stripe.")}</p>
      {status.isError && (
        <p className="error" role="alert">{tx("Payment status could not load.")}</p>
      )}
      <div className="flex gap" style={{ flexWrap: "wrap" }}>
        <button
          className="btn outline"
          disabled={status.isFetching}
          onClick={() => void status.refetch()}
        >{tx("Refresh status")}</button>
        {status.data && !status.data.chargesEnabled && (
          <button
            className="btn primary"
            disabled={!canEdit || start.isPending}
            onClick={() => start.mutate()}
          >
            {start.isPending
              ? tx("Opening payment setup…")
              : status.data.stripeAccountId
                ? tx("Continue payment setup")
                : tx("Connect payment account")}
          </button>
        )}
      </div>
      {start.isError && (
        <p className="error" role="alert">
          {errorText(start.error)}
        </p>
      )}
      <p className="commerce-note">{tx("Currency and availability are set per item. Workspace subscription billing remains in Settings.")}</p>
    </section>
  );
}

function ProductCard({
  product,
  onEdit,
  streamId,
  canEdit,
}: {
  product: Product;
  onEdit: () => void;
  streamId?: string;
  canEdit: boolean;
}) {
  usePageText();
  const qc = useQueryClient();
  const [confirmArchive, setConfirmArchive] = useState(false);
  const archive = useMutation({
    mutationFn: async () => api.delete(`/me/catalog/products/${product.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      void qc.invalidateQueries({ queryKey: ["catalog-products"] });
      setConfirmArchive(false);
    },
  });
  const detail = useQuery({
    queryKey: ["stream", streamId],
    queryFn: async () => (await api.get(`/me/streams/${streamId}`)).data,
    enabled: !!streamId,
  });
  const pinned = detail.data?.featuredProductId === product.id;
  const pin = useMutation({
    mutationFn: async () =>
      api.post(`/me/streams/${streamId}/feature-product`, {
        productId: pinned ? null : product.id,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stream", streamId] }),
  });
  const isService = kindOf(product) === "service",
    low = product.stock >= 0 && product.stock <= product.lowStockAt;
  return (
    <article className="card catalog-item">
      <div className="catalog-image">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={product.name} loading="lazy" />
        ) : isService ? (
          <SessionsIconNav />
        ) : (
          <CatalogIcon />
        )}
      </div>
      <div className="catalog-item-body">
        <div className="row-between">
          <span className="page-eyebrow">
            {isService ? tx("Service") : tx("Product")}
          </span>
          <span className={`badge ${low ? "warn" : ""}`}>
            {!product.active
              ? tx("Archived")
              : low
                ? product.stock === 0
                  ? tx("Unavailable")
                  : tx("Low availability")
                : tx("Active")}
          </span>
        </div>
        <h3>{product.name}</h3>
        <span className="muted">
          {product.externalId ||
            collectionOf(product) ||
            (isService ? tx("Arranged with customer") : tx("Catalog item"))}
        </span>
        <strong className="catalog-price">
          {fmt(product.priceCents, product.currency)}
          {product.compareAtCents != null &&
            product.compareAtCents > product.priceCents && (
              <del>{fmt(product.compareAtCents, product.currency)}</del>
            )}
        </strong>
        <span className="muted">
          {isService && details(product).durationMinutes
            ? ptx("{v0} minutes · ", {v0: (details(product).durationMinutes)})
            : ""}
          {product.stock < 0 ? tx("No stock limit") : ptx("{v0} available", {v0: (product.stock)})}
        </span>
        <div className="catalog-item-actions">
          <button
            className="btn outline sm"
            disabled={!canEdit}
            onClick={(e) => {
              e.currentTarget.focus();
              onEdit();
            }}
          >{tx("Edit")}</button>
          {streamId && product.active && (
            <button
              className="btn primary sm"
              disabled={
                !canEdit || pin.isPending || !detail.data || detail.isError
              }
              onClick={() => pin.mutate()}
            >
              {pinned ? tx("Unpin product") : tx("Pin to stream")}
            </button>
          )}
          {product.active && (
            <button
              className="btn ghost sm"
              disabled={!canEdit || archive.isPending}
              onClick={() => setConfirmArchive(true)}
            >{tx("Archive")}</button>
          )}
        </div>
        {(pin.isError || archive.isError) && (
          <p className="error" role="alert">
            {errorText(pin.error || archive.error)}
          </p>
        )}
      </div>
      {confirmArchive && (
        <DashboardDialog
          title={tx("Archive item?")}
          busy={archive.isPending}
          onClose={() => setConfirmArchive(false)}
        >
          <p>{ptx("“{v0}” will no longer be available for new purchases. Existing orders are retained.", {v0: (product.name)})}</p>
          {archive.isError && (
            <p className="error" role="alert">
              {errorText(archive.error)}
            </p>
          )}
          <div className="actions">
            <button
              className="btn outline"
              disabled={archive.isPending}
              onClick={() => setConfirmArchive(false)}
            >{tx("Cancel")}</button>
            <button
              className="btn"
              disabled={archive.isPending}
              onClick={() => archive.mutate()}
            >{tx("Archive item")}</button>
          </div>
        </DashboardDialog>
      )}
    </article>
  );
}

function EditProductModal({
  product,
  kind,
  collections,
  onClose,
}: {
  product?: Product;
  kind: Kind;
  collections: string[];
  onClose: () => void;
}) {
  usePageText();
  const qc = useQueryClient();
  const service = kind === "service";
  const meta = details(product);
  const [name, setName] = useState(product?.name ?? ""),
    [externalId, setExternalId] = useState(product?.externalId ?? ""),
    [description, setDescription] = useState(product?.description ?? ""),
    [imageUrl, setImageUrl] = useState(product?.imageUrl ?? "");
  const [currency, setCurrency] = useState(product?.currency ?? "USD"),
    [price, setPrice] = useState(
      product
        ? String(product.priceCents / 10 ** digits(product.currency))
        : "",
    ),
    [compare, setCompare] = useState(
      product?.compareAtCents != null
        ? String(product.compareAtCents / 10 ** digits(product.currency))
        : "",
    );
  const [unlimited, setUnlimited] = useState((product?.stock ?? -1) < 0),
    [stock, setStock] = useState(String(Math.max(0, product?.stock ?? 0))),
    [low, setLow] = useState(String(product?.lowStockAt ?? 5)),
    [active, setActive] = useState(product?.active ?? true);
  const [collection, setCollection] = useState(
      typeof meta.collection === "string" ? meta.collection : "",
    ),
    [duration, setDuration] = useState(String(meta.durationMinutes ?? 60)),
    [delivery, setDelivery] = useState(
      meta.delivery === "in-person" ? "in-person" : "online",
    ),
    [uploading, setUploading] = useState(false);
  const validMoney = (s: string) =>
    s.trim() !== "" &&
    Number.isFinite(Number(s)) &&
    Number(s) >= 0 &&
    /^\d+(\.\d+)?$/.test(s) &&
    (s.split(".")[1]?.length ?? 0) <= digits(currency) &&
    Math.round(Number(s) * 10 ** digits(currency)) <= 2147483647;
  const valid =
    name.trim().length > 0 &&
    validMoney(price) &&
    (!compare || validMoney(compare)) &&
    (unlimited || (/^\d+$/.test(stock) && Number(stock) <= 2147483647)) &&
    /^\d+$/.test(low) &&
    Number(low) <= 2147483647 &&
    (!service ||
      (/^\d+$/.test(duration) &&
        Number(duration) > 0 &&
        Number(duration) <= 10080 &&
        description.trim().length > 0));
  const save = useMutation({
    mutationFn: async () =>
      api.post("/me/catalog/products", {
        id: product?.id,
        name: name.trim(),
        externalId: externalId.trim() || undefined,
        description: description.trim(),
        imageUrl,
        priceCents: Math.round(Number(price) * 10 ** digits(currency)),
        compareAtCents: compare
          ? Math.round(Number(compare) * 10 ** digits(currency))
          : undefined,
        currency,
        stock: unlimited ? -1 : Number(stock),
        lowStockAt: Number(low),
        active,
        metadata: {
          ...product?.metadata,
          storefront: {
            ...meta,
            kind,
            collection: collection.trim(),
            ...(service
              ? {
                  durationMinutes: Number(duration),
                  delivery,
                  scheduling: "manual",
                }
              : {}),
          },
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["products"] });
      void qc.invalidateQueries({ queryKey: ["catalog-products"] });
      onClose();
    },
  });
  return (
    <DashboardDialog
      title={product ? tx("Edit {v0}", {v0: (kind)}) : tx("New {v0}", {v0: (kind)})}
      onClose={onClose}
      busy={save.isPending || uploading}
    >
      <form
        className="catalog-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !uploading && !save.isPending) save.mutate();
        }}
      >
        <fieldset className="catalog-fields" disabled={save.isPending}>
          <label className="label" htmlFor="product-name">{tx("Name")}</label>
          <input
            className="input"
            id="product-name"
            autoFocus
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <ImageUpload
            label={service ? tx("Service image") : tx("Product image")}
            value={imageUrl}
            onChange={setImageUrl}
            disabled={save.isPending}
            onBusy={setUploading}
          />
          <div className="grid-2">
            <div>
              <label className="label" htmlFor="product-price">{ptx("Price ({v0})", {v0: (currency)})}</label>
              <input
                className="input"
                id="product-price"
                type="number"
                min="0"
                step={10 ** -digits(currency)}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="product-currency">{tx("Currency")}</label>
              <select
                id="product-currency"
                className="input"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                {[
                  ...new Set([
                    currency,
                    "USD",
                    "AED",
                    "EUR",
                    "GBP",
                    "JPY",
                    "AUD",
                    "CAD",
                  ]),
                ].map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="label" htmlFor="product-description">
            {service
              ? tx("Description & delivery instructions")
              : tx("Description (optional)")}
          </label>
          <textarea
            id="product-description"
            className="input"
            rows={3}
            maxLength={2000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required={service}
          />
          {service && (
            <>
              <p className="muted">{tx("Tell customers how and when you will arrange the service. This purchase does not reserve an appointment.")}</p>
              <div className="grid-2">
                <div>
                  <label className="label" htmlFor="service-duration">{tx("Duration (minutes)")}</label>
                  <input
                    id="service-duration"
                    className="input"
                    type="number"
                    min="1"
                    max="10080"
                    value={duration}
                    onChange={(e) => setDuration(e.target.value)}
                  />
                </div>
                <div>
                  <label className="label" htmlFor="service-delivery">{tx("Delivery")}</label>
                  <select
                    className="input"
                    id="service-delivery"
                    value={delivery}
                    onChange={(e) => setDelivery(e.target.value)}
                  >
                    <option value="online">{tx("Online")}</option>
                    <option value="in-person">{tx("In person")}</option>
                  </select>
                </div>
              </div>
            </>
          )}
          <label className="label" htmlFor="item-collection">{tx("Collection (optional)")}</label>
          <input
            id="item-collection"
            className="input"
            list="catalog-collections"
            maxLength={80}
            value={collection}
            onChange={(e) => setCollection(e.target.value)}
            placeholder={tx("Choose or name a collection")}
          />
          <datalist id="catalog-collections">
            {collections.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <details className="catalog-advanced">
            <summary>{tx("Availability & additional details")}</summary>
            <label className="row gap">{ptx("{v0}No stock limit", {v0: (<input
                type="checkbox"
                checked={unlimited}
                onChange={(e) => setUnlimited(e.target.checked)}
              />)})}</label>
            {!unlimited && (
              <>
                <label className="label" htmlFor="item-stock">{tx("Available quantity")}</label>
                <input
                  className="input"
                  id="item-stock"
                  type="number"
                  min="0"
                  step="1"
                  value={stock}
                  onChange={(e) => setStock(e.target.value)}
                />
              </>
            )}
            <label className="label" htmlFor="item-low">{tx("Low availability threshold")}</label>
            <input
              className="input"
              id="item-low"
              type="number"
              min="0"
              step="1"
              value={low}
              onChange={(e) => setLow(e.target.value)}
            />
            <label className="label" htmlFor="item-sku">{tx("SKU / external ID")}</label>
            <input
              className="input"
              id="item-sku"
              maxLength={80}
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
            />
            <label className="label" htmlFor="item-compare">{ptx("Original price ({v0}, optional)", {v0: (currency)})}</label>
            <input
              className="input"
              id="item-compare"
              type="number"
              min="0"
              step={10 ** -digits(currency)}
              value={compare}
              onChange={(e) => setCompare(e.target.value)}
            />
            <label className="row gap">{ptx("{v0}Active and available for purchase", {v0: (<input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />)})}</label>
          </details>
          {price && !validMoney(price) && (
            <p className="error" role="alert">{ptx("Enter a valid price with up to {v0} decimal places.", {v0: (digits(currency))})}</p>
          )}
          {save.isError && (
            <p className="error" role="alert">
              {errorText(save.error)}
            </p>
          )}
          <div className="actions">
            <button
              type="button"
              className="btn outline"
              disabled={save.isPending || uploading}
              onClick={onClose}
            >{tx("Cancel")}</button>
            <button
              className="btn primary"
              disabled={!valid || save.isPending || uploading}
            >
              {save.isPending
                ? tx("Saving…")
                : product
                  ? ptx("Update {v0}", {v0: (kind)})
                  : ptx("Create {v0}", {v0: (kind)})}
            </button>
          </div>
        </fieldset>
      </form>
    </DashboardDialog>
  );
}
